import React, { useState, useEffect } from 'react';
import { X, Search, Hash, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const TYPE_LABELS = {
  actress: 'Actresses',
  studio: 'Studios',
  type: 'Types',
  theme: "Themes",
};
const TYPE_ORDER = ['actress', 'studio', 'type', 'theme'];

/*
 * TagExplorer — ontdekscherm. Toont alle beschikbare tags gegroepeerd per
 * type, met een zoekveld. Klik op een tag -> onSelectTag(name) opent de feed
 * gefilterd op die tag. Klik op zoeken -> onSearch(term).
 */
export default function TagExplorer({ onClose, onSelectTag, onSearch }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/tags`);
        const data = await res.json();
        setTags(data.tags || []);
      } catch (e) { /* stil */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = q
    ? tags.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()))
    : tags;

  const grouped = TYPE_ORDER.map((type) => ({
    type,
    label: TYPE_LABELS[type],
    items: filtered.filter((t) => t.tag_type === type),
  })).filter((g) => g.items.length > 0);

  const submitSearch = () => {
    if (q.trim()) { onSearch(q.trim()); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950 text-white flex flex-col">
      {/* Kop */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center gap-2">
          <Hash className="w-5 h-5 text-red-500" />
          <h2 className="font-bold text-lg">Discover</h2>
        </div>
        <button onClick={onClose} className="p-2 rounded hover:bg-zinc-800" aria-label="Sluiten">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Search bar */}
      <div className="px-5 py-4 border-b border-zinc-800">
        <div className="flex items-center gap-2 bg-zinc-800 rounded-lg px-3 py-2 max-w-xl">
          <Search className="w-4 h-4 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
            placeholder="Search by title, actress, studio or tag…"
            className="bg-transparent flex-1 outline-none text-sm"
            autoFocus
          />
          {q && (
            <button onClick={submitSearch}
              className="text-xs bg-red-600 hover:bg-red-700 px-3 py-1 rounded transition-colors">
              Zoeken
            </button>
          )}
        </div>
        {q && (
          <p className="text-xs text-zinc-500 mt-2">
            Druk Enter om te zoeken naar "{q}", of klik op een tag hieronder.
          </p>
        )}
      </div>

      {/* Tag-overzicht */}
      <div className="flex-1 overflow-y-auto p-5">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading tags…
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-center text-zinc-500 mt-10">
            {tags.length === 0
              ? 'Er zijn nog geen tags. Voeg tags toe via het beheerpaneel.'
              : `No tags found for "${q}".`}
          </div>
        ) : (
          <div className="space-y-8 max-w-5xl">
            {grouped.map((g) => (
              <div key={g.type}>
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-3">
                  {g.label}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => onSelectTag(t.name)}
                      className="group flex items-center gap-1.5 bg-zinc-800 hover:bg-red-600 px-3 py-1.5 rounded-full text-sm transition-colors"
                    >
                      <span>#{t.name}</span>
                      <span className="text-xs text-zinc-500 group-hover:text-red-200">
                        {t.usage_count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
